(ns frontend.view.settings2.permissions.field-settings.events
  (:require
   [frontend.view.settings2.permissions.field-settings.db :as db]
   [frontend.state.current-session :as session]
   [common.schema.registry :as registry]
   [common.schema.field-spec :as field-spec]
   [common.localization :as loc]
   [common.schema.data-types :as data-types]
   [common.roles :as roles]
   [common.util :as cu]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [clojure.set :as set]
   [clojure.string :as string]
   [common.permissions.field-permissions :as field-permissions]
   [frontend.logging :as logging]
   [common.teams :as teams]
   [common.schema.field :as schema-field]))

(defn hide-field?
  [{:keys [_tenant tenant-field-configurations entity-type show-inactive?
           fields-hidden-from-TA pipeline-id _entity-schema]
    :as   _props} [attribute fs]]
  (let [tenant-schema @session/tenant-schema
        min-zadmin-required (and (->> fs
                                      :field/default-authorization
                                      :access/read
                                      (roles/zadmin?))
                                 (->> fs
                                      :field/default-authorization
                                      :access/write
                                      (roles/zadmin?)))
        not-configurable (->> fs
                              :field/hidden-from-configuration?)
        default-field-visibility (-> fs
                                     :field/default-visibility
                                     :field-visibility)
        tenant-field-access-config (get-in tenant-field-configurations
                                           [entity-type attribute])
        tenant-field-setting (:field-settings/in-use-config tenant-field-access-config)
        tenant-field-setting
        (or tenant-field-setting default-field-visibility :not-in-use)
        field-inactive-for-tenant? (contains? field-permissions/NEGATIVE-IN-USE-VALUES
                                              tenant-field-setting)
        pipeline-setting (->> pipeline-id
                              (get @session/entities)
                              :pipeline/settings
                              (#(when %
                                  (get-in % [entity-type attribute :field-settings/in-use-config]))))
        pipeline-setting (or pipeline-setting tenant-field-setting)
        field-inactive-for-pipeline? (some #{pipeline-setting}
                                           field-permissions/NEGATIVE-IN-USE-VALUES)
        field-schema (registry/get-field-schema tenant-schema attribute)
        required-modules (:field/required-modules field-schema)
        missing-module? (not-every? (set @session/tenant-modules) required-modules)
        remove?
        (or
         missing-module?
         min-zadmin-required
         (:field/secondary-field? fs)
         not-configurable
         (and (= entity-type :entity.type/activity)
              (some #{attribute} [:common/attachments :common/external-id]))
         (and (= entity-type :entity.type/value-creation)
              ;; ONLY SHOW option fields in :entity.type/value-creation -> by customer request most of the fields are mandatory and it's in UI not in config
              (or (not (some #{(:field/data-type field-schema)}
                             [:data.type/ref-multi :data.type/ref-single]))
                  (not (some #{:entity.type/named-value}
                             (:field/entity-types field-schema)))))
         (and (not (session/zadmin?))
              (some #{attribute} fields-hidden-from-TA))
         (and (not show-inactive?)
              field-inactive-for-tenant?)
         (and pipeline-id
              (or (some #{attribute} fields-hidden-from-TA)
                  (and (not show-inactive?)
                       field-inactive-for-pipeline?)
                  field-inactive-for-tenant?)))]
    #_(when required-modules
        (js/console.log "REQUIRED : " required-modules " >> " remove?))

    remove?))

(defn ls-path
  [& rest]
  (concat
   [(:db/id @session/account)
    (:db/id @session/tenant)
    :settings-field-settings-view-settings]
   rest))

(defn copy-target?
  [column-key]
  (not (some #{column-key}
             [:field-settings/attribute
              :field-settings/zadmin-status
              :field-settings/field-name
              :field-settings/field-type
              :field-settings/in-use-config
              :field-settings/multiplier
              :field-settings/constraints
              :field-settings/selected-user-setting
              :field-settings/selected-user-setting-old])))

(defn column-keys
  [pipeline-id entity-type all-teams show-zadmin-columns?]
  (concat (when show-zadmin-columns?
            [:field-settings/attribute
             :field-settings/zadmin-status])
          [:field-settings/field-name
           :field-settings/field-type
           :field-settings/field-options
           :field-settings/in-use-config]
          (when-not (some #{entity-type} [:entity.type/activity :entity.type/value-creation])
            ;; DO not show team based access to these entities, as they are only configurable by the options management
            (->> all-teams
                 (map field-permissions/team-configuration-keyword)))
          (when (and (some #{entity-type} [:entity.type/oppo :entity.type/company])
                     (session/has-feature-access? :feature/buy-side-guest))
            [:field-settings/buy-side-guest])
          (when (and (not pipeline-id)
                     (some #{entity-type} [:entity.type/company])
                     (session/has-feature-access? :feature/sell-side-guest))
            [:field-settings/sell-side-guest])
          (when show-zadmin-columns?
            [:field-settings/selected-user-setting :field-settings/selected-user-setting-old])))

(def guest-columns #{:field-settings/buy-side-guest :field-settings/sell-side-guest})

(defn team-column?
  [k]
  (and (keyword? k)
       (= (namespace k) "field-settings.team")))

(defn permission-column?
  [k]
  (or (contains? guest-columns k)
      (team-column? k)))

(defn in-use-sort-value
  [entity]
  (case (:field-settings/in-use-config entity)
    :in-use-disabled 6
    :in-use 5
    :field-in-use 5
    :field-not-in-use 3
    :not-in-use 3
    :not-in-use-blocked 2
    1))

(defn permission-sort-value
  [entity k]
  (+ (* 100 (in-use-sort-value entity))
     (case (get entity k)
       :x 6
       :w 5
       :r 4
       :none 3
       false 2
       1)))

(defn field-settings-sort
  [entities sort-by-key]
  (if-not sort-by-key
    entities
    (some->>
     entities
     (sort-by
      (fn [entity]
        (let [sort-value (if (permission-column? sort-by-key)
                           (permission-sort-value entity sort-by-key)
                           (case sort-by-key
                             :field-settings/field-name (:field-settings/field-name entity)
                             :field-settings/field-type (:field-settings/field-type entity)
                             :field-settings/field-options (:field-settings/field-options entity)
                             :field-settings/attribute (:field-settings/attribute entity)
                             :field-settings/zadmin-status (:field-settings/zadmin-status entity)
                             :field-settings/in-use-config (in-use-sort-value entity)
                             (:field-settings/field-name entity)))]
          sort-value))))))

;;necessary arguments to use common grid functions
(defn setup-args
  [{:keys [pipeline-id all-teams selected-entity-type show-zadmin-columns?] :as _args}]
  (let [cols #(column-keys pipeline-id
                           selected-entity-type
                           all-teams
                           (and show-zadmin-columns? (session/zadmin?)))]
    {:state db/fields
     :re-sort? true
     :entity-type selected-entity-type
     :selected-columns-state db/selected-columns
     :ordered-columns-state db/ordered-columns
     :view-settings-state db/view-settings
     :column-widths-state db/column-widths
     :ls-path ls-path
     :column-keys cols
     :default-columns (cols)
     :mandatory? true
     :custom-sort-fn field-settings-sort}))

(defn calculate-grid-entities
  [{:keys [entity-type search show-inactive? field-settings-path active-teams
           settings-state zadmin-state grid-store pipeline-id _show-zadmin-columns?]}]
  (let [tenant-schema @session/tenant-schema
        configuration-schema (registry/get-schema tenant-schema entity-type)
        tenant (get @session/entities (:db/id @session/tenant))
        all-teams (->> @session/all-teams
                       (remove :common/disabled?))
        cleared (set (some->> zadmin-state
                              (deref)
                              :tenant/cleared-zadmin-conf-fields))
        tenant-field-configurations (:tenant/field-configurations @session/tenant)
        blacklisted (set/union (set (some->> zadmin-state
                                             deref
                                             :tenant/blacklisted-fields))
                               (apply disj
                                      (set (:tenant/blacklisted-fields tenant))
                                      (set/union (set (some->> zadmin-state
                                                               (deref)
                                                               :tenant/whitelisted-custom-fields))
                                                 cleared)))
        whitelisted (set/union (set (some->> zadmin-state
                                             deref
                                             :tenant/whitelisted-custom-fields))
                               (apply disj
                                      (set (:tenant/whitelisted-custom-fields tenant))
                                      (set/union (set (some->> zadmin-state
                                                               (deref)
                                                               :tenant/blacklisted-fields))
                                                 cleared)))
        ;; blaclisted + not-in-use-blocked - whitelisted - in-use-disabled - mandatory
        fields-hidden-from-TA (->> blacklisted
                                   (set)
                                   (set/union (->> (registry/get-schema tenant-schema entity-type)
                                                   (filter (fn [[_attr fs]]
                                                             (= :not-in-use-blocked
                                                                (->> fs
                                                                     :field/default-visibility
                                                                     :field-visibility))))
                                                   (map first)
                                                   (set)))
                                   (cu/flip set/difference whitelisted)
                                   (cu/flip set/difference
                                            (->> (registry/get-schema tenant-schema entity-type)
                                                 (filter (fn [[_attr fs]]
                                                           (= :in-use-disabled
                                                              (->> fs
                                                                   :field/default-visibility
                                                                   :field-visibility))))
                                                 (map first)
                                                 (set))))
        sorted-entities
        (->>
         configuration-schema
         (remove (fn [[attribute fs]]
                   (hide-field? {:tenant         tenant
                                 :tenant-field-configurations tenant-field-configurations
                                 :entity-type    entity-type
                                 :show-inactive? show-inactive?
                                 :fields-hidden-from-TA fields-hidden-from-TA
                                 :pipeline-id    pipeline-id
                                 :entity-schema  configuration-schema}
                                [attribute fs])))
         (map
          (fn [[attribute fs]]
            (let [temp-value (get-in @settings-state [entity-type attribute])
                  backend-path (concat field-settings-path [entity-type attribute])
                  tenant-field-access-config
                  (or (get-in @session/entities backend-path)
                      (get-in @session/tenant
                              [:tenant/field-configurations entity-type attribute]))
                  field-schema (registry/get-field-schema tenant-schema attribute)
                  default-auth (:field/default-authorization field-schema)
                  default-visiblity (:field/default-visibility field-schema)
                  mandatory? (or (field-spec/system-required-or-legacy-always-mandatory-field?
                                  field-schema)
                                 (= :in-use-disabled
                                    (-> field-schema
                                        :field/default-visibility
                                        :field-visibility)))
                  default-user-access (cond
                                        (roles/has-access? :role/user (:access/create default-auth))
                                        :x
                                        (roles/has-access? :role/user (:access/write default-auth))
                                        :w
                                        (roles/has-access? :role/user (:access/read default-auth))
                                        :r
                                        :else
                                        :none)
                  default-ssg-setting
                  (cond (roles/has-access? :role/guest-sell-side (:access/create default-auth))
                        :x
                        (roles/has-access? :role/guest-sell-side (:access/write default-auth))
                        :w
                        (roles/has-access? :role/guest-sell-side (:access/read default-auth))
                        :r
                        :else
                        :none)
                  default-bsg-setting
                  (cond (roles/has-access? :role/guest-buy-side (:access/create default-auth))
                        :x
                        (roles/has-access? :role/guest-buy-side (:access/write default-auth))
                        :w
                        (roles/has-access? :role/guest-buy-side (:access/read default-auth))
                        :r
                        :else
                        :none)
                  calculated-in-use-value-kw (if mandatory?
                                               :in-use-disabled
                                               (or (:field-settings/in-use-config temp-value)
                                                   (:field-settings/in-use-config
                                                    tenant-field-access-config)
                                                   (:field-visibility default-visiblity)
                                                   :not-in-use))
                  temp-in-use? (boolean (some #{calculated-in-use-value-kw}
                                              field-permissions/POSITIVE-IN-USE-VALUES))
                  res-in-use
                  {:field-settings/in-use-config calculated-in-use-value-kw
                   :field-settings/attribute     attribute
                   :field-settings/zadmin-status (cond (some #{attribute} whitelisted) :whitelist
                                                       (some #{attribute} fields-hidden-from-TA)
                                                       :blacklist
                                                       :else :default)
                   :field-settings/field-name    (let [fs (registry/get-field-schema
                                                           tenant-schema
                                                           attribute)
                                                       field-name (or (when fs
                                                                        (loc/field-name attribute
                                                                                        fs))
                                                                      (loc/tr attribute))]
                                                   field-name)
                   :field-settings/field-options (loc/tr :manage-options-title)
                   :field-settings/field-type    (cond
                                                   (schema-field/tag-field? attribute)
                                                   (loc/tr :tag)
                                                   (= :common/attachments attribute)
                                                   (loc/tr :file)
                                                   (and (= :data.type/ref-single
                                                           (:field/data-type fs))
                                                        (:named-value-attribute fs))
                                                   (loc/tr :option-single)
                                                   (and (= :data.type/ref-single
                                                           (:field/data-type fs))
                                                        (:named-value-attribute fs))
                                                   (loc/tr :option-multi)
                                                   :else
                                                   (->> fs
                                                        :field/data-type
                                                        (get data-types/zapflow-data-types)
                                                        :translations
                                                        :en))}
                  res
                  (merge
                   res-in-use
                   {:field-settings/buy-side-guest  (and temp-in-use?
                                                         (or (:field-settings/buy-side-guest
                                                              temp-value)
                                                             (:field-settings/buy-side-guest
                                                              tenant-field-access-config)
                                                             default-bsg-setting))
                    :field-settings/sell-side-guest (and temp-in-use?
                                                         (or (:field-settings/sell-side-guest
                                                              temp-value)
                                                             (:field-settings/sell-side-guest
                                                              tenant-field-access-config)
                                                             default-ssg-setting))}
                   (->>
                    active-teams
                    (map (fn [t]
                           (let [kw (field-permissions/team-configuration-keyword t)
                                 team-permission (and (:field-settings/in-use-config res-in-use)
                                                      (or (get temp-value kw)
                                                          (get tenant-field-access-config kw)
                                                          default-user-access))]
                             [kw team-permission])))
                    (into {})))
                  res-with-test
                  (merge
                   res
                   (when-let [test-user (:selected-user @grid-store)]
                     (let [user-account (get @session/entities test-user)
                           user-teams (teams/resolve-account-teams user-account all-teams)
                           max-team-permission
                           (if-not (:field-settings/in-use-config res-in-use)
                             :not-in-use
                             (->> user-teams
                                  (map (fn [team]
                                         (get res
                                              (field-permissions/team-configuration-keyword team))))
                                  (sort-by (fn [p]
                                             (case p
                                               :none 0
                                               :r 1
                                               :w 2
                                               :x 3)))
                                  (last)))
                           max-old-team-permission (field-permissions/resolve-field-access
                                                    user-teams
                                                    tenant-field-access-config
                                                    default-user-access
                                                    logging/error)]
                       {:field-settings/selected-user-setting     max-team-permission
                        :field-settings/selected-user-setting-old max-old-team-permission})))]
              res-with-test)))
         (doall))
        search (when (string? search)
                 (->> search
                      string/trim
                      string/lower-case))
        sorted-entities (if (string/blank? search)
                          sorted-entities
                          (->> sorted-entities
                               (filter (fn [{:keys [field-settings/field-name]}]
                                         (let [lc-name (->> field-name
                                                            str
                                                            string/trim
                                                            string/lower-case)]
                                           (string/includes? lc-name search))))))]
    (swap! db/fields assoc :grid-entities sorted-entities)
    ;; We must deref cursor to force Sync to r/atom
    (boolean @db/fields)))

(defn cleanup
  [component-state settings-state zadmin-state]
  (swap! db/fields dissoc
    :loaded
    :schema
    :entities
    :entity-types
    :view-settings
    :sorted-grid-entities
    :search
    :grid-entities)
  (remove-watch session/tenant :field-settings)
  (remove-watch session/session :field-settings-watch)
  (remove-watch component-state :field-settings-entity-watch)
  (remove-watch settings-state :field-settings-state-watch)
  (remove-watch zadmin-state :field-zadmin-state-watch))

(defn setup
  [{:keys [pipeline-id field-settings-path table-state settings-state zadmin-state grid-store
           show-zadmin-columns?]
    :as   _args}]
  (let [active-teams (->> @session/all-teams
                          (remove :common/disabled?))
        calc! (fn []
                (let [{:keys [entity-type search show-inactive?]} @table-state]
                  (calculate-grid-entities {:entity-type          entity-type
                                            :search               search
                                            :show-inactive?       show-inactive?
                                            :pipeline-id          pipeline-id
                                            :grid-store           grid-store
                                            :field-settings-path  field-settings-path
                                            :active-teams         active-teams
                                            :settings-state       settings-state
                                            :zadmin-state         zadmin-state
                                            :show-zadmin-columns? show-zadmin-columns?})))]
    #_(when-not (empty? (ls/get-in (ls-path)))
        (reset! db/view-settings (ls/get-in (ls-path))))
    ;;always setup with sorted by field name:
    (reset! db/view-settings {:sort-by :field-settings/field-name :sort-direction "ASC"})
    (calc!)
    (grid/recalculate (setup-args {:all-teams   active-teams
                                   :pipeline-id pipeline-id
                                   :selected-entity-type (:entity-type @table-state)
                                   :show-zadmin-columns? show-zadmin-columns?}))
    (add-watch
     session/session
     :field-settings-watch
     (fn [_k _a old new]
       (let [new-teams (->> new
                            :entities
                            vals
                            (cu/only-entity :entity.type/team)
                            (remove :common/disabled?))
             old-teams (->> old
                            :entities
                            vals
                            (cu/only-entity :entity.type/team)
                            (remove :common/disabled?))
             new-settings (get-in new field-settings-path)
             old-settings (get-in old field-settings-path)
             new-blacklist (get-in new
                                   [:entities (:db/id @session/tenant) :tenant/blacklisted-fields])
             old-blacklist (get-in old
                                   [:entities (:db/id @session/tenant) :tenant/blacklisted-fields])
             new-whitelist
             (get-in new [:entities (:db/id @session/tenant) :tenant/whitelisted-custom-fields])
             old-whitelist
             (get-in old [:entities (:db/id @session/tenant) :tenant/whitelisted-custom-fields])]
         (when (or (not= new-teams old-teams)
                   (not= new-settings old-settings)
                   (not= new-blacklist old-blacklist)
                   (not= new-whitelist old-whitelist))
           (calc!)
           (grid/recalculate (setup-args {:pipeline-id pipeline-id
                                          :all-teams   new-teams
                                          :selected-entity-type (:entity-type @table-state)
                                          :show-zadmin-columns? show-zadmin-columns?}))))))

    (add-watch
     table-state
     :field-settings-entity-watch
     (fn [_k _a old new]
       (let [new-type (:entity-type new)
             old-type (:entity-type old)
             new-show (:show-inactive? new)
             old-show (:show-inactive? old)
             new-search (:search new)
             old-search (:search old)
             all-teams (->> @session/all-teams
                            (remove :common/disabled?))]
         (when (or (not= new-type old-type)
                   (not= new-search old-search)
                   (not= new-show old-show))
           (calc!)
           (grid/recalculate (setup-args {:pipeline-id pipeline-id
                                          :all-teams   all-teams
                                          :selected-entity-type (:entity-type @table-state)
                                          :show-zadmin-columns? show-zadmin-columns?}))))))

    (add-watch
     settings-state
     :field-settings-state-watch
     (fn [_k _a old new]
       (let [all-teams (->> @session/all-teams
                            (remove :common/disabled?))]
         (when-not (= old new)
           (calc!)
           (grid/recalculate (setup-args {:pipeline-id pipeline-id
                                          :all-teams   all-teams
                                          :selected-entity-type (:entity-type @table-state)
                                          :show-zadmin-columns? show-zadmin-columns?}))))))

    (add-watch
     zadmin-state
     :field-zadmin-state-watch
     (fn [_k _a old new]
       (let [all-teams (->> @session/all-teams
                            (remove :common/disabled?))]
         (when-not (= old new)
           (calc!)
           (grid/recalculate (setup-args {:pipeline-id pipeline-id
                                          :all-teams   all-teams
                                          :selected-entity-type (:entity-type @table-state)
                                          :show-zadmin-columns? show-zadmin-columns?}))))))


    (swap! db/fields assoc :loaded true)))


#_(defn set-zadmin-status!
  [field-attribute v]
  (let [tenant (get @session/entities (:db/id @session/tenant))
        update! (fn [tenant-field set-fn]
                  (let [new-value (set-fn (set (get tenant tenant-field)) field-attribute)]
                    (swap! session/entities assoc-in [(:db/id tenant) tenant-field] new-value)))]
    (case v
      :whitelist (do
                   (update! :tenant/whitelisted-custom-fields conj)
                   (update! :tenant/blacklisted-fields disj)
                   (update! :tenant/cleared-zadmin-conf-fields disj))
      :blacklist (do
                   (update! :tenant/whitelisted-custom-fields disj)
                   (update! :tenant/blacklisted-fields conj)
                   (update! :tenant/cleared-zadmin-conf-fields disj))
      :normal (do
                (update! :tenant/whitelisted-custom-fields disj)
                (update! :tenant/blacklisted-fields disj)
                (update! :tenant/cleared-zadmin-conf-fields conj))
      (logging/error "Unknown field setting : " v))))

#_(defn set-team-config!
  [_field-settings-path field-attribute v]
  (let [tenant (get @session/entities (:db/id @session/tenant))
        update! (fn [tenant-field set-fn]
                  (let [new-value (set-fn (set (get tenant tenant-field)) field-attribute)]
                    (swap! session/entities assoc-in [(:db/id tenant) tenant-field] new-value)))]
    (case v
      :whitelist (do
                   (update! :tenant/whitelisted-custom-fields conj)
                   (update! :tenant/blacklisted-fields disj))
      :blacklist (do
                   (update! :tenant/whitelisted-custom-fields disj)
                   (update! :tenant/blacklisted-fields conj))
      :normal (do
                (update! :tenant/whitelisted-custom-fields disj)
                (update! :tenant/blacklisted-fields disj))
      (logging/error "Unknown field setting : " v))))

#_(defn set-in-use!
  [_field-settings-path field-attribute v]
  (let [tenant (get @session/entities (:db/id @session/tenant))
        update! (fn [tenant-field set-fn]
                  (let [new-value (set-fn (set (get tenant tenant-field)) field-attribute)]
                    (swap! session/entities assoc-in [(:db/id tenant) tenant-field] new-value)))]
    (case v
      :whitelist (do
                   (update! :tenant/whitelisted-custom-fields conj)
                   (update! :tenant/blacklisted-fields disj))
      :blacklist (do
                   (update! :tenant/whitelisted-custom-fields disj)
                   (update! :tenant/blacklisted-fields conj))
      :normal (do
                (update! :tenant/whitelisted-custom-fields disj)
                (update! :tenant/blacklisted-fields disj))
      (logging/error "Unknown field setting : " v))))
